import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowRight, Lock, Sparkles } from "lucide-react";
import Disclaimer from "@/components/Disclaimer";

export default function Home() {
    return (
        <div className="min-h-screen bg-rt-base text-rt-text relative overflow-hidden">
            <Helmet>
                <title>RealTalk AI — Stuck in your head?</title>
                <meta property="og:title" content="RealTalk AI — Stuck in your head?" />
                <meta
                    property="og:description"
                    content="Say what you can't say anywhere else. Get real clarity in minutes — completely anonymous."
                />
                <meta name="twitter:title" content="RealTalk AI — Stuck in your head?" />
                <meta
                    name="twitter:description"
                    content="Say what you can't say anywhere else. Get real clarity in minutes — completely anonymous."
                />
            </Helmet>
            {/* Header */}
            <header className="relative z-10 px-6 sm:px-10 py-6 flex items-center justify-between">
                <div
                    className="flex items-center gap-2 font-serif text-lg tracking-tight"
                    data-testid="home-logo"
                >
                    <span className="inline-block w-2 h-2 rounded-full bg-rt-accent" />
                    <span>RealTalk</span>
                    <span className="text-rt-muted font-sans text-xs ml-1 uppercase tracking-[0.2em]">
                        ai
                    </span>
                </div>
                <Link
                    to="/chat"
                    className="text-sm text-rt-muted hover:text-rt-text transition-colors"
                    data-testid="home-header-chat-link"
                >
                    Open chat →
                </Link>
            </header>

            {/* Hero */}
            <main className="relative z-10 px-6 sm:px-10">
                <section className="max-w-3xl mx-auto pt-20 sm:pt-28 pb-20 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rt-border bg-rt-surface/70 text-xs text-rt-muted mb-10"
                        data-testid="home-pill"
                    >
                        <Sparkles className="w-3 h-3 text-rt-accent" />
                        Anonymous · Private · No sign-up
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.05 }}
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
                        data-testid="home-headline"
                    >
                        Stuck in your{" "}
                        <span className="italic text-rt-accent">head?</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mt-8 text-base sm:text-lg text-rt-muted max-w-xl mx-auto leading-relaxed"
                        data-testid="home-subheadline"
                    >
                        Say what you can&rsquo;t say anywhere else. Get real
                        clarity in minutes—completely anonymous.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.25 }}
                        className="mt-12 flex flex-col sm:flex-row gap-4 items-center justify-center"
                    >
                        <Link
                            to="/chat"
                            className="group inline-flex items-center gap-2 rounded-full bg-[#B392F0] hover:bg-[#C8B1F5] text-black font-medium px-8 py-4 transition-all duration-300 hover:-translate-y-[2px] shadow-[0_0_0_1px_rgba(179,146,240,0.25),0_12px_32px_-8px_rgba(179,146,240,0.45)]"
                            data-testid="start-chat-btn"
                        >
                            Start Talking Now
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </Link>
                        <span className="text-xs text-rt-dim flex items-center gap-1.5">
                            <Lock className="w-3 h-3" /> Nothing is stored
                            against your name
                        </span>
                    </motion.div>
                </section>

                {/* How it works */}
                <section className="max-w-4xl mx-auto pb-24">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        {[
                            {
                                n: "01",
                                t: "Say it plainly",
                                d: "Unfiltered. Messy. Real. Whatever is rattling around.",
                            },
                            {
                                n: "02",
                                t: "Get perspective",
                                d: "Not orders. Options, blind spots, and honest reframes.",
                            },
                            {
                                n: "03",
                                t: "Leave lighter",
                                d: "Clearer head, one grounded next step.",
                            },
                        ].map((s, i) => (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.5,
                                    delay: 0.3 + i * 0.08,
                                }}
                                className="rounded-2xl border border-rt-border bg-rt-surface/60 p-6 backdrop-blur-sm"
                                data-testid={`home-step-${s.n}`}
                            >
                                <div className="font-serif text-rt-accent text-sm mb-3">
                                    {s.n}
                                </div>
                                <div className="font-serif text-xl mb-2 tracking-tight">
                                    {s.t}
                                </div>
                                <div className="text-sm text-rt-muted leading-relaxed">
                                    {s.d}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </main>

            {/* Ambient background */}
            <div className="pointer-events-none absolute inset-0 z-0 opacity-60 rt-hero-bg" />
            <div className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-b from-rt-base/80 via-rt-base/90 to-rt-base" />

            <Disclaimer />
        </div>
    );
}
